import { LoginSocial } from 'components/Blocks/LoginForm/LoginSocial';
import Button from 'components/Forms/Button/Button';
import Checkbox from 'components/Forms/Checkbox/Checkbox';
import Form from 'components/Forms/Form/Form';
import { GdprConsentsWrapper } from 'components/Forms/GdprConsentsWrapper/GdprConsentsWrapper';
import ChoiceFormLine from 'components/Forms/Lib/ChoiceFormLine/ChoiceFormLine';
import { showSuccessMessage } from 'components/Helpers/Toasts';
import SimpleLayout from 'components/Layout/SimpleLayout/SimpleLayout';
import { LoginFormPopup } from 'components/Pages/Order/ContactInformation/LoginFormPopup';
import { Account } from 'components/Pages/Registration/Account/Account';
import Company from 'components/Pages/Registration/Company';
import {
    RegistrationFormType,
    useRegistrationForm,
    useRegistrationFormMeta,
} from 'components/Pages/Registration/formMeta';
import User from 'components/Pages/Registration/User';
import { useGdprConsents } from 'connectors/gdpr/gdprConsents';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { GdprActionTypeEnumApi, LoginTypeEnumApi, useConfirmEmailMutationApi } from 'graphql/generated';
import { getSocialLoginDataCookie } from 'helpers/cookies/getUserConsentCookie';
import { blurInput } from 'helpers/forms/blurInput';
import { clearForm } from 'helpers/forms/clearForm';
import { formUnmasking } from 'helpers/forms/formUnmasking';
import { handleFormErrors } from 'helpers/forms/handleFormErrors';
import { isDomainLocale } from 'helpers/isDomainLocale';
import { useRegistration } from 'hooks/auth/useRegistration';
import { useErrorPopup } from 'hooks/forms/useErrorPopup';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useWatch } from 'react-hook-form';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { BreadcrumbItemType } from 'types/breadcrumb';
import { SocialLoginDataType } from 'types/cookie';
import { GtmMessageOriginType } from 'types/gtm';
import { setTokensToCookie } from 'utils/Auth/TokensFromCookies';

type RegistrationProps = {
    breadcrumbs: BreadcrumbItemType[];
};

const contentHeadingTwClass = 'font-medium';
const contentSectionTwClass =
    'mb-7 pb-6 border-b border-borderLight lg:flex lg:flex-row lg:mb-11 lg:pb-9 md:last:border-none';
const contentRightTwClass = 'md:w-[340px] md:ml-auto md:mr-[70px]';
const contentBorderedSectionTwClass =
    'relative pt-[30px] pb-[10px] pl-[20px] pr-[20px] md:p-0 border-[5px] border-borderLighter md:border-none rounded-md z-1';
const buttonWrapperTwClass = 'w-full flex justify-center mt-7';
const whiteWrapperTwClass = 'm-[-30px] md:m-0 pt-[30px] md:pt-0 bg-white md:bg-grayLight';

export const getRegistrationLoginType = (socialLoginData: SocialLoginDataType | null) => {
    if (!socialLoginData) {
        return LoginTypeEnumApi.WebApi;
    }

    if (!socialLoginData.loginType) {
        return LoginTypeEnumApi.WebApi;
    }

    return socialLoginData.loginType;
};

const Registration: FC<RegistrationProps> = ({ breadcrumbs }) => {
    const { t } = useTranslation();
    const updatePortalContent = useSessionStore((s) => s.updatePortalContent);
    const gdprConsents = useGdprConsents(GdprActionTypeEnumApi.RegisterApi, null);
    const register = useRegistration();
    const [confirmEmailResult, confirmEmail] = useConfirmEmailMutationApi();
    const { cartUuid } = usePersistStore((s) => s);
    const [formProviderMethods, defaultValues] = useRegistrationForm();
    const formMeta = useRegistrationFormMeta(formProviderMethods);
    const router = useRouter();
    const { defaultLocale } = useDomainConfig();
    const isPortugalLocale = isDomainLocale(defaultLocale, 'pt');
    const socialLoginData = getSocialLoginDataCookie();
    const { updateUserState, isLoginPopupOpen } = usePersistStore((s) => s);
    const { isUserLoggedIn } = useCurrentUserData();
    const [emailValue] = useWatch({
        name: [formMeta.fields.email.name],
        control: formProviderMethods.control,
    });

    useEffect(() => {
        if (isLoginPopupOpen && !isUserLoggedIn) {
            updatePortalContent(<LoginFormPopup email={emailValue} />);
        }

        return () => {
            updatePortalContent(null);
        };
    }, [emailValue, isLoginPopupOpen, isUserLoggedIn, updatePortalContent]);

    useErrorPopup(formProviderMethods, formMeta.fields, undefined, GtmMessageOriginType.other);

    const onRegistrationHandler: SubmitHandler<RegistrationFormType> = async (data) => {
        blurInput();
        let companyNumber = data.companyNumber;

        if (data.companyCustomer && isPortugalLocale) {
            companyNumber = '';
        }

        const registrationError = await register({
            ...data,
            companyNumber,
            password: data.passwordFirst,
            country: data.country.value,
            district: data.district.value || null,
            postcode: formUnmasking(data.postcode, 'postcode'),
            loginType: getRegistrationLoginType(socialLoginData) as LoginTypeEnumApi,
            cartUuid,
        });

        handleFormErrors(registrationError, formProviderMethods, t, formMeta.messages.error, undefined);
        clearForm(registrationError, formProviderMethods, defaultValues);
    };

    const customerValue = useWatch({
        name: formMeta.fields.companyCustomer.name,
        control: formProviderMethods.control,
    });

    const confirmOnlyValue = router.query.confirmOnly;
    const confirmEmailValue = router.query.email;
    const confirmHashValue = router.query.hash;

    const isConfirmation =
        confirmOnlyValue !== undefined && confirmEmailValue !== undefined && confirmHashValue !== undefined;
    if (
        isConfirmation &&
        !confirmEmailResult.fetching &&
        confirmEmailResult.data === undefined &&
        confirmEmailResult.error === undefined
    ) {
        const emailString = confirmEmailValue.toString();
        const hashString = confirmHashValue.toString();

        confirmEmail({
            email: emailString,
            hash: hashString,
        }).then((result) => {
            if (result.data === undefined) {
                return;
            }

            const accessToken = result.data.ConfirmEmail.tokens.accessToken;
            const refreshToken = result.data.ConfirmEmail.tokens.refreshToken;

            const uuid = result.data.ConfirmEmail.uuid;
            const email = result.data.ConfirmEmail.email;

            const signIn = result.data.ConfirmEmail.signIn;

            if (signIn) {
                updateUserState({ userUuid: uuid });
                updateUserState({ userEmail: email });
                setTokensToCookie(accessToken, refreshToken);
                showSuccessMessage(formMeta.messages.successAndLogged);
            }

            router.push('/');
        });
    }

    return (
        <>
            <SimpleLayout breadcrumb={breadcrumbs} heading={t('Tescoma Club account registration')}>
                <FormProvider {...formProviderMethods}>
                    <Form onSubmit={formProviderMethods.handleSubmit(onRegistrationHandler)}>
                        <div className={contentSectionTwClass}>
                            <h6 className={contentHeadingTwClass}>{t('Account registration')}</h6>
                            <div className={contentRightTwClass}>
                                <Account />
                                <LoginSocial shouldOverwriteCustomerUserCart cartUuid={cartUuid} />
                            </div>
                        </div>

                        <div className={contentSectionTwClass}>
                            <h6 className={contentHeadingTwClass}>{t('Contact details')}</h6>
                            <div className={contentRightTwClass}>
                                <User />
                            </div>
                        </div>

                        {customerValue === true && (
                            <div className={contentSectionTwClass}>
                                <h6 className={contentHeadingTwClass}>{t('Company data')}</h6>
                                <div className={contentRightTwClass}>
                                    <Company />
                                </div>
                            </div>
                        )}

                        <div className={whiteWrapperTwClass} data-testid="gdprConsents">
                            <GdprConsentsWrapper
                                render={(handler, value) => (
                                    <div className={contentBorderedSectionTwClass}>
                                        {gdprConsents?.map(({ type, isRequired, text }, index) => {
                                            return (
                                                <div key={type} className={contentRightTwClass}>
                                                    <ChoiceFormLine>
                                                        <Checkbox
                                                            checked={value.includes(type)}
                                                            name={`gdprConsents.${index}`}
                                                            required={isRequired}
                                                            value={type}
                                                            label={
                                                                <span
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: text ?? '',
                                                                    }}
                                                                />
                                                            }
                                                            onChange={handler(type)}
                                                        />
                                                    </ChoiceFormLine>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            />
                            <div className={buttonWrapperTwClass}>
                                <Button type="submit">{t('Sign up')}</Button>
                            </div>
                        </div>
                    </Form>
                </FormProvider>
            </SimpleLayout>
        </>
    );
};

export default Registration;
